import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import SiteFooter from '../components/SiteFooter';
import { ArrowLeft, Download, Calendar, Clock, CheckCircle, Circle, ChevronRight, Loader2, RotateCw } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SessionArchive = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  // Track per-session recovery state (shows a spinner next to "Try to
  // recover notes") so repeated clicks don't fire multiple retries.
  const [recovering, setRecovering] = useState({});
  // Polling timer ref — one shared interval does the work for every
  // session with a Processing… badge. Cleared on unmount.
  const pollTimerRef = useRef(null);

  useEffect(() => {
    fetchSessions();
    // Mount-only: load session list once. Re-fetch is triggered by user actions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll finalize status for any session rendered with the "Processing…"
  // badge. When each one lands we re-fetch the list so the badge gets
  // replaced with the real summary + wins. We poll every 3s per the
  // spec — fast enough to feel responsive, slow enough to stay cheap.
  useEffect(() => {
    const processingSessions = sessions.filter((s) => s.processing);
    if (processingSessions.length === 0) {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return undefined;
    }
    if (pollTimerRef.current) return undefined;
    pollTimerRef.current = setInterval(async () => {
      const token = localStorage.getItem('sage_token');
      if (!token) return;
      let anyReady = false;
      // Fire checks in parallel — on completion we'll refresh once.
      await Promise.all(
        processingSessions.map(async (s) => {
          try {
            const { data } = await axios.get(
              `${API}/coaching/agent/finalize/status/${s.session_id}`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (data?.status === 'ready' || data?.status === 'failed') {
              anyReady = true;
            }
          } catch (_) { /* keep polling */ }
        }),
      );
      if (anyReady) {
        fetchSessions();
      }
    }, 3000);
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
    // fetchSessions is a stable component-scoped helper; polling is
    // keyed off `sessions` which already encodes any list change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions]);

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('sage_token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API}/sessions/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSessions(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      toast.error('Failed to load session history');
      setLoading(false);
    }
  };

  const fetchSessionDetail = async (sessionId) => {
    try {
      const token = localStorage.getItem('sage_token');
      const response = await axios.get(`${API}/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSessionDetail(response.data);
      setSelectedSession(sessionId);
    } catch (error) {
      console.error('Failed to fetch session detail:', error);
      toast.error('Failed to load session details');
    }
  };

  const downloadSession = async (sessionId) => {
    try {
      const token = localStorage.getItem('sage_token');
      const response = await axios.get(`${API}/sessions/${sessionId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sage_session_${sessionId.slice(0, 8)}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Session downloaded');
    } catch (error) {
      console.error('Failed to download session:', error);
      toast.error('Failed to download session');
    }
  };

  // "Try to recover notes" — re-enqueues the background finalize task
  // for a session whose transcript fetch gave up. Shown only on rows
  // flagged `transcript_fetch_failed`.
  const recoverSession = async (sessionId, ev) => {
    ev?.stopPropagation?.();
    if (recovering[sessionId]) return;
    setRecovering((r) => ({ ...r, [sessionId]: true }));
    try {
      const token = localStorage.getItem('sage_token');
      await axios.post(
        `${API}/coaching/agent/finalize/retry/${sessionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Trying again — notes will appear shortly');
      // Soft refresh so the badge flips from failed → processing.
      fetchSessions();
    } catch (err) {
      toast.error('Could not retry right now. Please try again in a bit.');
      setRecovering((r) => ({ ...r, [sessionId]: false }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="overlay-noise" />
        <p className="text-zinc-500 relative z-10">Loading sessions...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <div className="overlay-noise" />
      
      <div className="relative z-10">
        {/* Header */}
        <div className="border-b border-white/5 bg-stone-950/50 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-transparent border border-white/20 text-white hover:bg-white/5 rounded-sm px-4 py-2 transition-all"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 
                  className="text-2xl font-light tracking-tight text-zinc-100"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  Session Archive
                </h1>
                <p className="text-xs font-mono uppercase tracking-widest text-zinc-400 mt-1">
                  Your coaching journey
                </p>
              </div>
            </div>
            <ThemeToggle className="border border-white/20 rounded-sm" />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Sessions List — hidden on mobile when a session is open so
                the detail overlay owns the viewport without the list
                bleeding under it. */}
            <div className={`lg:col-span-1 space-y-3 ${selectedSession ? 'hidden lg:block' : ''}`}>
              <h2 className="text-sm font-mono uppercase tracking-widest text-zinc-500 mb-4">
                Past Sessions ({sessions.length})
              </h2>
              
              {sessions.length === 0 ? (
                <div className="bg-stone-950/50 border border-white/5 rounded-sm p-8 text-center">
                  <p className="text-zinc-500 text-sm">No sessions yet</p>
                  <Button
                    onClick={() => navigate('/live-coaching')}
                    className="mt-4 frost-button rounded-sm px-4 py-2 text-sm"
                  >
                    Start Your First Session
                  </Button>
                </div>
              ) : (
                sessions.map((session) => (
                  <div
                    key={session.session_id}
                    onClick={() => !session.processing && !session.transcript_fetch_failed && fetchSessionDetail(session.session_id)}
                    className={`bg-stone-950/50 border rounded-sm p-4 transition-all ${
                      session.processing || session.transcript_fetch_failed
                        ? 'border-white/5 cursor-default'
                        : selectedSession === session.session_id
                          ? 'border-white/30 bg-white/5 cursor-pointer'
                          : 'border-white/5 hover:border-white/10 cursor-pointer'
                    }`}
                    data-testid={`session-${session.session_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 text-xs text-zinc-500 mb-2">
                          <Calendar className="w-3 h-3" />
                          {formatDate(session.created_at)}
                          {session.processing && (
                            <span
                              data-testid={`processing-badge-${session.session_id}`}
                              className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-white/5 text-zinc-400 text-[10px] font-mono uppercase tracking-wider"
                            >
                              <Loader2 className="w-3 h-3 animate-spin" />
                              Processing…
                            </span>
                          )}
                          {session.transcript_fetch_failed && !session.processing && (
                            <button
                              type="button"
                              data-testid={`recover-link-${session.session_id}`}
                              onClick={(e) => recoverSession(session.session_id, e)}
                              disabled={!!recovering[session.session_id]}
                              className="ml-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-amber-200/80 hover:text-amber-100 disabled:opacity-60"
                            >
                              {recovering[session.session_id] ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <RotateCw className="w-3 h-3" />
                              )}
                              Try to recover notes
                            </button>
                          )}
                        </div>
                        <p className="text-sm text-white line-clamp-2">
                          {session.summary_text || 'Session summary'}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-zinc-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {session.duration_minutes || 0}m
                          </span>
                          {session.todos_extracted?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              {session.todos_extracted.length} tasks
                            </span>
                          )}
                        </div>
                      </div>
                      {!session.processing && !session.transcript_fetch_failed && (
                        <ChevronRight className="w-4 h-4 text-zinc-400" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Session Detail — on mobile this becomes a full column
                that takes over the viewport once a card is tapped; on
                desktop it's the right-hand 2/3 pane. A mobile-only
                "Back to list" row sits at the top so the user can
                return without hunting for the browser back button. */}
            <div className={`lg:col-span-2 ${selectedSession ? '' : 'hidden lg:block'}`}>
              {selectedSession && sessionDetail ? (
                <div className="bg-stone-950/50 border border-white/5 rounded-sm p-6 space-y-6">
                  {/* Mobile-only: back to session list */}
                  <button
                    type="button"
                    onClick={() => { setSelectedSession(null); setSessionDetail(null); }}
                    data-testid="back-to-list-mobile"
                    className="lg:hidden flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-400 hover:text-white -mt-2 -ml-1"
                  >
                    <ArrowLeft className="w-3 h-3" />
                    Back to sessions
                  </button>
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 
                        className="text-xl font-light text-white mb-1"
                        style={{ fontFamily: 'Cormorant Garamond, serif' }}
                      >
                        Session Details
                      </h3>
                      <p className="text-xs text-zinc-500">
                        {formatDate(sessionDetail.summary?.created_at)}
                      </p>
                    </div>
                    <Button
                      onClick={() => downloadSession(selectedSession)}
                      className="bg-white/10 text-white hover:bg-white/20 rounded-sm px-4 py-2 text-sm flex items-center gap-2"
                      data-testid="download-session-btn"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </Button>
                  </div>

                  {/* Summary */}
                  <div>
                    <h4 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">
                      Summary
                    </h4>
                    <p className="text-sm text-zinc-300 leading-relaxed">
                      {sessionDetail.summary?.summary_text || 'No summary available.'}
                    </p>
                  </div>

                  {sessionDetail.summary?.wellbeing_line && (
                    <div data-testid="session-wellbeing">
                      <h4 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">
                        How you were that day
                      </h4>
                      <p className="text-sm text-zinc-400 leading-relaxed">
                        {sessionDetail.summary.wellbeing_line}
                      </p>
                    </div>
                  )}

                  {/* Key Insights */}
                  {sessionDetail.summary?.key_insights?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">
                        Key Insights
                      </h4>
                      <ul className="space-y-2">
                        {sessionDetail.summary.key_insights.map((insight, i) => (
                          <li key={`insight-${i}-${(insight || '').slice(0, 16)}`} className="text-sm text-zinc-400 flex items-start gap-2">
                            <span className="text-white/50 mt-1">•</span>
                            {insight}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Action Items */}
                  {sessionDetail.todos?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">
                        Action Items
                      </h4>
                      <ul className="space-y-2">
                        {sessionDetail.todos.map((todo) => (
                          <li 
                            key={todo.todo_id} 
                            className="flex items-start gap-2 text-sm"
                          >
                            {todo.status === 'completed' ? (
                              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            ) : (
                              <Circle className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
                            )}
                            <span className={todo.status === 'completed' ? 'text-zinc-500 line-through' : 'text-zinc-300'}>
                              {todo.description}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Conversation */}
                  {sessionDetail.session?.interactions?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">
                        Conversation
                      </h4>
                      <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                        {sessionDetail.session.interactions.map((interaction, i) => {
                          // Clean up user input
                          let userText = interaction.user_input || '';
                          if (userText.includes("The user just said:")) {
                            userText = userText.replace("The user just said: '", "").split("'\n\n")[0];
                          }
                          // Use interaction.timestamp if present; otherwise stable composite key
                          const key = interaction.timestamp || `${i}-${(interaction.sage_response || '').slice(0, 16)}`;
                          return (
                            <div key={key} className="space-y-2">
                              <div className="bg-white/5 rounded-sm p-3">
                                <p className="text-xs text-zinc-500 mb-1">You</p>
                                <p className="text-sm text-zinc-300">{userText}</p>
                              </div>
                              <div className="bg-stone-900/50 rounded-sm p-3 border-l-2 border-white/20">
                                <p className="text-xs text-zinc-500 mb-1">SAGE</p>
                                <p className="text-sm text-zinc-300">{interaction.sage_response}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-stone-950/50 border border-white/5 rounded-sm p-12 text-center">
                  <p className="text-zinc-500 text-sm">
                    Select a session to view details
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
};

export default SessionArchive;
